import axios from 'axios';
import ItineraryPackageLoader, {
    type ItineraryPackageLoaderResult,
} from '../ItineraryPackageLoader';
import HotelPackage from '../hotel/HotelPackage';
import FlightPackageSet from '../trip/flight/FlightPackageSet';
import HotelRoomType from '../hotel/HotelRoomType';
import RoomsPackage from '../hotel/RoomsPackage';
import BundlePackage from './BundlePackage';
import {
    type BundlePackageLoaderConfig,
    type BundleRoomsPaxCount,
} from './types';
import { type HotelSearchView } from '@clients/HotelApi';
import {
    type HotelFilterData,
    type HotelSearchHotelTypeViewCumulated,
} from '@itinerary/hotel/HotelModuleTypes';
import { RequestCanceledException } from '@utils/itinerary';
import {
    BundleApiBundleGetAllAvailablePost,
    type GetAvailableFlightsResponse,
} from '@clients/BundleApi';

/**
 * The result from _getPackages method of BundlePackageLoader
 */
export interface BundlePackageLoaderResult
    extends ItineraryPackageLoaderResult<BundlePackage> {
    list: BundlePackage[];
    filterMetaData: HotelFilterData;
}

export default class BundlePackageLoader extends ItineraryPackageLoader<BundlePackage> {
    private readonly _fromDate: string;
    private readonly _toDate: string;
    private readonly _originCode: string;
    private readonly _destinationCode: string;
    private readonly rooms: BundleRoomsPaxCount[];

    constructor({
        fromDate,
        toDate,
        originCode,
        destinationCode,
        rooms,
        url,
    }: BundlePackageLoaderConfig) {
        super({ url });
        this._fromDate = fromDate;
        this._toDate = toDate;
        this._originCode = originCode;
        this._destinationCode = destinationCode;
        this.rooms = rooms;
    }

    override get date() {
        return new Date(this._fromDate);
    }

    get adultCount(): number {
        return this.rooms.reduce((count, curr) => count + curr.adultCount, 0);
    }

    get childCount(): number {
        return this.rooms.reduce((count, curr) => count + curr.childCount, 0);
    }

    get infantCount(): number {
        return this.rooms.reduce((count, curr) => count + curr.infantCount, 0);
    }

    override compareTo(other: BundlePackageLoaderConfig): boolean {
        if (this._originCode !== other.originCode) {
            return false;
        }
        if (this._destinationCode !== other.destinationCode) {
            return false;
        }
        if (this._fromDate.toString() !== other.fromDate.toString()) {
            return false;
        }
        if (this._toDate.toString() !== other.toDate.toString()) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (
            this.rooms.reduce(
                (a, c) =>
                    `${a}${c.adultCount}-${c.childCount}-${c.infantCount}`,
                ''
            ) !==
            other.rooms?.reduce(
                (a, c) =>
                    `${a}${c.adultCount}-${c.childCount}-${c.infantCount}`,
                ''
            )
        ) {
            return false;
        }
        return true;
    }

    override async *_getPackages(
        _axiosParams?: any
    ): AsyncGenerator<BundlePackageLoaderResult> {
        try {
            const { data } = await BundleApiBundleGetAllAvailablePost(
                {
                    getAvailableBundleRequest: {
                        originId: Number(this._originCode),
                        destinationId: Number(this._destinationCode),
                        departureDate: this._fromDate,
                        returnDate: this._toDate,
                        roomsRequests: this.rooms,
                    },
                },
                {
                    timeout: 600000,
                }
            );
            const minPrice = (data.availableBundleResponses ?? []).reduce(
                (min, curr) =>
                    (curr.payable ?? 0) < min ? curr.payable ?? 0 : min,
                Infinity
            );
            const maxPrice = (data.availableBundleResponses ?? []).reduce(
                (max, curr) =>
                    (curr.payable ?? 0) > max ? curr.payable ?? 0 : max,
                0
            );
            const list = (data.availableBundleResponses ?? []).map(res => {
                const hotelPackage = new HotelPackage({
                    hotel: res.hotel as HotelSearchView,
                    adultCount: this.adultCount,
                    childCount: this.childCount,
                    infantCount: this.infantCount,
                    fromDate: this._fromDate,
                    toDate: this._toDate,
                });
                hotelPackage.isForBundlePackage = true;
                const roomsPackages = (res.bundleRoomTypeResponse ?? []).map(
                    rp =>
                        new RoomsPackage({
                            rooms: rp.map(
                                r =>
                                    new HotelRoomType({
                                        ...r,
                                        checkinHour: hotelPackage.checkinHour,
                                        checkoutHour: hotelPackage.checkinHour,
                                    })
                            ),
                        })
                );
                return new BundlePackage({
                    hotelPackage,
                    departurePackageSet: this._getFlightPackageSet(
                        res.Flights!.FlightResponse!
                    ),
                    returnPackageSet: this._getFlightPackageSet(
                        res.Flights!.ReturnFlightResponse!
                    ),
                    rooms: this.rooms,
                    singlePrice: res.totalPrice ?? 0,
                    singlePayable: res.payable ?? 0,
                    sumTotalPrice: res.sumPrice ?? 0,
                    roomsPackages,
                });
            });
            yield {
                list,
                filterMetaData: {
                    maxPrice,
                    minPrice,
                    accommodationTypes: (
                        data.hotelTypes?.map(type => ({
                            ...type,
                            title: type.title?.replace(/ \d+ ستاره/, ''),
                        })) || []
                    ).reduce((previousValue, currentValue) => {
                        const find = previousValue.find(
                            val => val.title === currentValue.title
                        );
                        if (find) {
                            find.cumulatedIds.push(currentValue.id ?? 0);
                            return previousValue;
                        } else {
                            return [
                                ...previousValue,
                                {
                                    ...currentValue,
                                    cumulatedIds: [currentValue.id ?? 0],
                                },
                            ];
                        }
                    }, [] as HotelSearchHotelTypeViewCumulated[]),
                    amenities: data.hotelAmenities ?? [],
                    serviceLevels: [],
                    specialOffers: [],
                },
            };
        } catch (ex) {
            if (axios.isCancel(ex)) {
                throw new RequestCanceledException();
            }
            throw ex;
        }
    }

    private _getFlightPackageSet(
        _result: GetAvailableFlightsResponse
    ): FlightPackageSet {
        // const loaderResult = new FlightPackageLoaderResultFactory({
        //     apiResult: result as GetFlightsResponseDto,
        //     adultCount: this.adultCount,
        //     childCount: this.childCount,
        //     infantCount: this.infantCount,
        //     isForBundle: true,
        // });
        // packageSet._list = loaderResult.list;
        return new FlightPackageSet(null as any, {
            airline: null,
            duration: null,
            price: null,
            stopCount: null,
            stops: null,
            time: null,
            classOfService: [],
            labels: null,
            flightNumber: '',
            baggage: [],
        });
    }
}
